import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Switch from 'components/atoms/Switch'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim, toLower, includes, find } from 'lodash'
import countriesList from 'pages/company/modals/countries'
import CityLocationPicker from './CityLocationPicker'

const AddCityModal = ({ onSuccess, close, id = null, close: customClose, onMapUiLanguageChange }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [countries, setCountries] = useState([])
  const [mapOpen, setMapOpen] = useState(false)
  const [mapLanguage, setMapLanguage] = useState('en')
  const [formData, setFormData] = useState({
    city: '',
    cityAr: '',
    country: '',
    lat: '',
    lon: '',
    active: true,
  })

  useEffect(() => {
    fetchCountries()
    if (id) {
      fetchCityData()
    }
  }, [id])

  const selectedCountry = useMemo(() => {
    if (!formData.country) return null
    return find(countries, item => String(item.id) === String(formData.country)) || null
  }, [countries, formData.country])

  const selectedCountryCode = useMemo(() => {
    const englishName = selectedCountry?.englishName || ''
    if (!englishName) return ''
    const match = find(countriesList, item => toLower(item.name) === toLower(englishName))
    return match?.id || ''
  }, [selectedCountry])

  const fetchCountries = async () => {
    try {
      const response = await apiGet('countries', { pageSize: 500, pageNumber: 1 })
      if (response?.data?.countries) {
        setCountries(
          response.data.countries
            .filter(c => c.active !== false)
            .map(c => ({
              id: c._id,
              name: isArabic && c.countryAr ? c.countryAr : c.country,
              englishName: c.country,
            }))
        )
      } else {
        setCountries([])
      }
    } catch (error) {
      console.error('Error fetching countries:', error)
      setCountries([])
    }
  }

  const fetchCityData = async () => {
    try {
      const response = await apiGet(`city/${id}`)
      if (response.success) {
        const data = response.data
        const countryId = data.country?._id || data.country || ''
        setFormData({
          city: data.city || '',
          cityAr: data.cityAr || '',
          country: countryId,
          lat: data.lat != null ? String(data.lat) : '',
          lon: data.lon != null ? String(data.lon) : '',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addCityModal.messages.failedToFetchCityData'))
      }
    } catch (error) {
      console.error('Error fetching city data:', error)
      notify.error(t('addCityModal.messages.failedToFetchCityData'))
    }
  }

  const handleCountryChange = selectedValue => {
    setFormData(prevData => ({
      ...prevData,
      country: selectedValue?.id || '',
      city: '',
      cityAr: '',
      lat: '',
      lon: '',
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const closeMapPicker = () => {
    setMapOpen(false)
    onMapUiLanguageChange?.(null)
  }

  const openMapPicker = language => {
    if (!formData.country) {
      notify.error(t('addCityModal.messages.selectCountryBeforeLocation'))
      return
    }
    setMapLanguage(language)
    setMapOpen(true)
    onMapUiLanguageChange?.(language)
  }

  const handleLocationSelect = location => {
    setFormData(prevData => ({
      ...prevData,
      city: location.city || '',
      cityAr: location.cityAr || '',
      lat: location.lat != null ? String(location.lat) : '',
      lon: location.lon != null ? String(location.lon) : '',
    }))
    closeMapPicker()
  }

  const validateCoordinates = () => {
    const lat = parseFloat(formData.lat)
    const lon = parseFloat(formData.lon)

    if (formData.lat === '' || formData.lon === '' || Number.isNaN(lat) || Number.isNaN(lon)) {
      notify.error(t('addCityModal.messages.coordinatesRequired'))
      return null
    }
    if (lat < -90 || lat > 90) {
      notify.error(t('addCityModal.messages.latitudeInvalid'))
      return null
    }
    if (lon < -180 || lon > 180) {
      notify.error(t('addCityModal.messages.longitudeInvalid'))
      return null
    }

    return { lat, lon }
  }

  const handleSubmit = async () => {
    if (!formData.country) {
      notify.error(t('addCityModal.messages.countryRequired'))
      return
    }
    if (!trim(formData.city)) {
      notify.error(t('addCityModal.messages.cityNameRequired'))
      return
    }
    if (!trim(formData.cityAr)) {
      notify.error(t('addCityModal.messages.cityArabicNameRequired'))
      return
    }

    const coordinates = validateCoordinates()
    if (!coordinates) return

    const payload = {
      city: formData.city,
      cityAr: formData.cityAr,
      country: formData.country,
      active: formData.active,
      lat: coordinates.lat,
      lon: coordinates.lon,
    }

    try {
      let response
      if (id) {
        response = await apiPut(`city/${id}`, payload)
        if (response.success) {
          notify.success(t('addCityModal.messages.cityUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('city', payload)
        if (response.success) {
          notify.success(t('addCityModal.messages.cityAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) {
          customClose()
        }
        close()
      } else {
        const message = toLower(response.message || '')
        const isDuplicate = includes(message, 'already exists') || includes(message, 'duplicate')
        notify.error(isDuplicate ? t('addCityModal.messages.cityAlreadyExists') : response.message || t('addCityModal.messages.operationFailed'))
      }
    } catch (error) {
      console.error('Error saving city:', error)
      notify.error(t('addCityModal.messages.failedToSaveCity'))
    }
  }

  const handleCancel = () => {
    if (mapOpen) {
      closeMapPicker()
      return
    }
    if (customClose) {
      customClose()
    }
    close()
  }

  if (mapOpen) {
    return (
      <CityLocationPicker
        key={`${mapLanguage}-${selectedCountryCode || formData.country}`}
        language={mapLanguage}
        countryCode={selectedCountryCode}
        countryName={selectedCountry?.englishName || selectedCountry?.name}
        initialLat={formData.lat ? parseFloat(formData.lat) : undefined}
        initialLon={formData.lon ? parseFloat(formData.lon) : undefined}
        onLocationSelect={handleLocationSelect}
        onClose={closeMapPicker}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addCityModal.labels.country')}
        </label>
        <Selection onChange={handleCountryChange} title={t('addCityModal.placeholders.selectCountry')} list={countries} value={formData.country || ''} className="w-full" />
      </div>
      <div>
        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addCityModal.labels.city')}
        </label>
        <button
          type="button"
          onClick={() => openMapPicker('en')}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left cursor-pointer hover:border-dark-500 focus:outline-none focus:ring-1 focus:ring-dark-500 focus:border-dark-500 sm:text-sm"
        >
          <span className={`block truncate ${formData.city ? 'text-gray-900' : 'text-gray-400'}`}>{formData.city || t('addCityModal.placeholders.selectCityOnMap')}</span>
        </button>
      </div>
      <div>
        <label htmlFor="cityAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addCityModal.labels.cityArabic')}
        </label>
        <button
          type="button"
          dir="rtl"
          onClick={() => openMapPicker('ar')}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-right cursor-pointer hover:border-dark-500 focus:outline-none focus:ring-1 focus:ring-dark-500 focus:border-dark-500 sm:text-sm"
        >
          <span className={`block truncate text-right ${formData.cityAr ? 'text-gray-900' : 'text-gray-400'}`}>
            {formData.cityAr || t('addCityModal.placeholders.selectCityOnMapArabic')}
          </span>
        </button>
      </div>
      <div>
        <label htmlFor="lat" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addCityModal.labels.latitude')}
        </label>
        <input
          type="text"
          name="lat"
          value={formData.lat}
          readOnly
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700 sm:text-sm"
          placeholder={t('addCityModal.placeholders.autoLatitude')}
        />
      </div>
      <div>
        <label htmlFor="lon" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addCityModal.labels.longitude')}
        </label>
        <input
          type="text"
          name="lon"
          value={formData.lon}
          readOnly
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700 sm:text-sm"
          placeholder={t('addCityModal.placeholders.autoLongitude')}
        />
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addCityModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addCityModal.buttons.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addCityModal.buttons.updateCity') : t('addCityModal.buttons.addCity')}
        </button>
      </div>
    </div>
  )
}

export default AddCityModal
